/**
 * AccountMembers = consumer "Team" tenancy: flat owner/member
 * (`account_members(owner_id, member_id, accepted_at, removed_at)`), all
 * owner-scoped content resolves via `current_account_owner_id()`. This is
 * DELIBERATELY a separate, non-merging system from OrganizationService's
 * hierarchical franchise/enterprise "Organization" (owner/admin/corporate
 * roles, org-scoped RLS) — per the committed north-star decision (see
 * docs/superpowers/specs/2026-07-14-team-org-clarity-and-account-context-design.md
 * if present, or memory `project_team_org_clarity_phase1_2026-07-14`). Do NOT
 * build a shared MembershipService abstraction over the two.
 *
 * Note: the user-facing noun for this table/service is "Team" everywhere in
 * the UI and API endpoints (`/api/team/*`), even though the table and this
 * service are named `account_members`/`AccountMembersService`. That mismatch
 * is intentional and documented here so it isn't "fixed" by a future rename.
 */
import { ServiceError } from '../base/ServiceError';
import type { ServiceResponse } from '../base/ServiceTypes';

import { postService } from './PostService';
import type { PostRow } from './PostService';

import { supabase } from '@/integrations/supabase/client';
import { resolveOwnerId } from '@/lib/identity/resolveOwnerId';

export type AccountRole = 'owner' | 'member';

export interface AccountOwnerProfile {
  id: string;
  fullName: string | null;
  email: string | null;
  avatarUrl: string | null;
}

export interface AccountMemberProfile {
  fullName: string | null;
  email: string | null;
  avatarUrl: string | null;
}

export interface AccountMemberRecord {
  id: string;
  memberId: string;
  role: AccountRole;
  invitedEmail: string;
  invitedAt: string;
  acceptedAt: string | null;
  profile: AccountMemberProfile | null;
  isSelf: boolean;
  /**
   * Consumer draft-approval (2026-07-15): when false, this member's posts
   * route to the owner's review queue before publishing (see
   * account_members.trusted, mirrors org_members.trusted).
   */
  trusted: boolean;
  /**
   * Task 1b (2026-08-23): 'viewer' seats are free — they never consume a
   * seat against the plan's invite cap (see server/api/team/_shared/seat-cap.ts
   * and the account_members_cap_trigger/account_invites_cap_trigger DB
   * triggers, both seat_kind-aware as of this task).
   */
  seatKind: 'full' | 'viewer';
}

export interface AccountInviteRecord {
  id: string;
  invitedEmail: string;
  invitedAt: string;
  expiresAt: string;
  seatKind: 'full' | 'viewer';
}

export interface AccountRosterResponse {
  isOwner: boolean;
  ownerId: string;
  /** Plan-gated seat cap (teammates beyond the owner) — see server/api/team/_shared/seat-cap.ts. */
  maxInvites: number;
  /** View-only internal collaborators do not consume an operator seat. */
  maxViewerInvites: number;
  seatCapKnown?: boolean;
  ownerProfile: AccountOwnerProfile | null;
  members: AccountMemberRecord[];
  invites: AccountInviteRecord[];
  /**
   * Consumer draft-approval (2026-07-15): account-wide "require approval for
   * every member" switch (mirrors organizations.mandated_review), sourced
   * from user_profiles.require_member_approval on the owner's row.
   */
  requireMemberApproval: boolean;
}

export interface InviteResult {
  id: string;
  expiresAt: string;
  acceptUrl: string;
  sentTo: string;
  /** True when the owner set a password: the person is already on the team, no email was sent. */
  accountCreated?: boolean;
}

interface AuthedRequestInit extends RequestInit {
  accessToken?: string;
}

async function authedFetch(path: string, init: AuthedRequestInit = {}): Promise<Response> {
  const { accessToken, headers, ...requestInit } = init;
  const { data } = await supabase.auth.getSession();
  const token = accessToken ?? data.session?.access_token;
  if (!token) {
    throw new Error('Please sign in to continue.');
  }
  return fetch(path, {
    ...requestInit,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(headers ?? {}),
    },
  });
}

async function asJson<T>(res: Response): Promise<T> {
  const text = await res.text();
  const parsed = text ? (JSON.parse(text) as unknown) : {};
  if (!res.ok) {
    const errObj = parsed as { error?: string; detail?: string };
    const errMsg = errObj.detail
      ? `${errObj.error} (${errObj.detail})`
      : (errObj.error ?? `Request failed (${res.status})`);
    throw new Error(errMsg);
  }
  return parsed as T;
}

export async function fetchRoster(): Promise<AccountRosterResponse> {
  const res = await authedFetch('/api/team/members');
  return asJson<AccountRosterResponse>(res);
}

/** Matches `invite.post.ts`'s inline `workspaceGrants` body-field type — the
 * canonical `WorkspaceRole` union lives in `useWorkspaceAccess.ts` (a hook,
 * one layer up from this service), so it's re-declared here rather than
 * imported to keep services from depending on hooks. */
export interface InviteWorkspaceGrant {
  workspaceId: string;
  role: 'admin' | 'editor' | 'viewer';
}

export async function sendInvite(
  email: string,
  avatarUrl?: string,
  // Optional third param (not folded into a single options object) so every
  // existing 2-arg call site and the `AccountMembersService.test.ts`
  // fixtures asserting the exact POST body keep working unchanged — omitted
  // fields stay `undefined` and JSON.stringify drops them, same as
  // `avatar_url` already did.
  grants?: {
    grantAllWorkspaces?: boolean;
    workspaceGrants?: InviteWorkspaceGrant[];
    resend?: boolean;
    password?: string;
  },
): Promise<InviteResult> {
  const res = await authedFetch('/api/team/invite', {
    method: 'POST',
    body: JSON.stringify({
      email,
      avatar_url: avatarUrl,
      grantAllWorkspaces: grants?.grantAllWorkspaces,
      workspaceGrants: grants?.workspaceGrants,
      resend: grants?.resend,
      password: grants?.password,
    }),
  });
  return asJson<InviteResult>(res);
}

export async function revokeInvite(inviteId: string): Promise<{ revoked: true }> {
  const res = await authedFetch(`/api/team/invite/${inviteId}`, {
    method: 'DELETE',
  });
  return asJson<{ revoked: true }>(res);
}

export async function removeMember(memberRowId: string): Promise<{ removed: true }> {
  const res = await authedFetch(`/api/team/member/${memberRowId}`, {
    method: 'DELETE',
  });
  return asJson<{ removed: true }>(res);
}

export async function leaveTeam(): Promise<{ left: true }> {
  const res = await authedFetch('/api/team/leave', { method: 'POST' });
  return asJson<{ left: true }>(res);
}

export interface InvitePreview {
  status: 'valid' | 'invalid';
  reason?: 'revoked' | 'accepted' | 'expired';
  invitedEmail?: string;
  expiresAt?: string;
  ownerName?: string;
  ownerEmailMasked?: string | null;
  ownerAvatarUrl?: string | null;
}

export async function lookupInviteByToken(token: string): Promise<InvitePreview> {
  const res = await fetch(`/api/team/invite/by-token?token=${encodeURIComponent(token)}`);
  if (!res.ok) return { status: 'invalid' };
  const body = (await res.json()) as InvitePreview;
  return body;
}

/**
 * Creates the invitee's account already confirmed — but only when `proof` (the
 * `k` parameter that exists solely in the EMAILED link) shows they opened the
 * invitation email. The token alone cannot: the owner holds it too.
 *
 * - 'created'  the account exists and is confirmed; sign in with the password.
 * - 'exists'   a confirmed account already holds this address (sign in instead).
 * - 'confirm'  no proof (e.g. a copied link): nothing was created; use the normal
 *              sign-up, which emails a real confirmation link.
 */
export async function signUpForInvite(
  token: string,
  proof: string,
  password: string,
  name: string,
): Promise<'created' | 'exists' | 'confirm'> {
  const res = await fetch('/api/team/invite/signup', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, proof, password, name }),
  });
  if (res.status === 409) return 'exists';
  const body = await asJson<{ ok: boolean; confirmationRequired?: boolean }>(res);
  return body.confirmationRequired ? 'confirm' : 'created';
}

export interface AcceptInviteResult {
  ownerId: string;
  ownerName: string;
  ownerEmail: string | null;
  ownerAvatarUrl: string | null;
}

export async function acceptInvite(
  token: string,
  accessToken?: string,
): Promise<AcceptInviteResult> {
  const res = await authedFetch('/api/team/invite/accept', {
    method: 'POST',
    accessToken,
    body: JSON.stringify({ token }),
  });
  return asJson<AcceptInviteResult>(res);
}

/**
 * Consumer draft-approval (2026-07-15): everything below this line talks to
 * Supabase directly (not the /api/team/* routes above) because it operates
 * on `social_posts`/`account_members`/`user_profiles`, the same tables
 * PostService/OrganizationService already query directly from the client.
 */

async function resolveAccountOwnerId(): Promise<string> {
  try {
    return await resolveOwnerId('');
  } catch (error) {
    throw new ServiceError(
      'QUERY_ERROR',
      error instanceof Error ? error.message : 'Account owner lookup failed',
      undefined,
      error,
    );
  }
}

/**
 * `is_account_owner()` is true for anyone who is NOT an accepted member of
 * some account (owners, solo users, org-only users); false for an accepted
 * account member. Used as an app-layer guard — belt-and-suspenders on top
 * of (not a replacement for) the DB-side gate.
 */
async function isAccountOwner(): Promise<boolean> {
  const { data, error } = await supabase.rpc('is_account_owner');
  if (error) {
    throw new ServiceError(
      'QUERY_ERROR',
      `Account owner check failed: ${error.message}`,
      undefined,
      error,
    );
  }
  return data === true;
}

const OWNER_ONLY_ERROR = 'Only the account owner can change approval settings';
const OWNER_ONLY_APPROVE_ERROR = 'Only the account owner can approve a member post';
const OWNER_ONLY_REJECT_ERROR = 'Only the account owner can reject a member post';

/**
 * Owner-scoped review queue: posts on the current shared account awaiting
 * approval. No member-id `IN`-list needed (unlike the org version) — every
 * consumer post already carries `user_id = ownerId` under shared-account RLS.
 */
export async function getPendingReviewPosts(): Promise<ServiceResponse<PostRow[]>> {
  try {
    const ownerId = await resolveAccountOwnerId();

    // Scope to the active workspace, keeping unattributed (legacy,
    // pre-workspace-stamping) rows visible — see
    // PostService.resolveActiveBrandKitId for the full rationale. Copied
    // inline (not a class method here) since this file exposes plain
    // functions, not a service class.
    let activeKitId: string | null = null;
    try {
      const { data: kitData, error: kitError } = await supabase.rpc('get_effective_brand_kit');
      if (!kitError && Array.isArray(kitData) && kitData.length > 0) {
        activeKitId = (kitData[0] as { id?: string }).id ?? null;
      }
    } catch {
      activeKitId = null;
    }

    let query = supabase
      .from('social_posts')
      .select('*')
      .eq('user_id', ownerId)
      .eq('status', 'pending_review');
    if (activeKitId) {
      query = query.or(`brand_kit_id.eq.${activeKitId},brand_kit_id.is.null`);
    }

    const { data, error } = await query.order('created_at', { ascending: false });

    if (error) {
      return { data: null, error: ServiceError.fromPostgrestError(error) };
    }
    return { data: (data ?? []) as PostRow[], error: null };
  } catch (err) {
    return { data: null, error: ServiceError.fromSupabaseError(err) };
  }
}

/**
 * Set a member's `trusted` flag. Owner-only (app-layer guard); RLS on
 * `account_members` is not itself sufficient defense-in-depth here, so this
 * checks `is_account_owner()` before writing.
 */
export async function updateMemberTrust(
  memberId: string,
  trusted: boolean,
): Promise<ServiceResponse<void>> {
  try {
    const owner = await isAccountOwner();
    if (!owner) {
      return { data: null, error: new ServiceError('FORBIDDEN', OWNER_ONLY_ERROR) };
    }
    const ownerId = await resolveAccountOwnerId();
    const { error } = await supabase
      .from('account_members')
      .update({ trusted })
      .eq('id', memberId)
      .eq('owner_id', ownerId);

    if (error) {
      return { data: null, error: ServiceError.fromPostgrestError(error) };
    }
    return { data: null, error: null };
  } catch (err) {
    return { data: null, error: ServiceError.fromSupabaseError(err) };
  }
}

/**
 * Toggle the account-wide "require approval for every member" switch
 * (Consumer analogue of organizations.mandated_review). Owner-only.
 */
export async function setRequireMemberApproval(value: boolean): Promise<ServiceResponse<void>> {
  try {
    const owner = await isAccountOwner();
    if (!owner) {
      return { data: null, error: new ServiceError('FORBIDDEN', OWNER_ONLY_ERROR) };
    }
    const ownerId = await resolveAccountOwnerId();
    const { error } = await supabase
      .from('user_profiles')
      .update({ require_member_approval: value })
      .eq('id', ownerId);

    if (error) {
      return { data: null, error: ServiceError.fromPostgrestError(error) };
    }
    return { data: null, error: null };
  } catch (err) {
    return { data: null, error: ServiceError.fromSupabaseError(err) };
  }
}

/**
 * Approve a pending-review post. Delegates the actual status transition to
 * `postService.approvePendingPost` (tier-agnostic, reused as-is) — this
 * wrapper only adds the app-layer owner guard. The DB trigger's own
 * approve-transition guard already blocks member self-approval via
 * `is_account_owner()`, but that function returns `false` for a user who
 * happens to be an accepted member of a *different* account (a known,
 * documented edge case) — this app-layer check is belt-and-suspenders on
 * top of that, not a replacement for it.
 */
export async function approvePendingPost(postId: string): Promise<ServiceResponse<PostRow>> {
  try {
    const owner = await isAccountOwner();
    if (!owner) {
      return { data: null, error: new ServiceError('FORBIDDEN', OWNER_ONLY_APPROVE_ERROR) };
    }
  } catch (err) {
    return { data: null, error: ServiceError.fromSupabaseError(err) };
  }
  return postService.approvePendingPost(postId);
}

/**
 * Reject a pending-review post (bounce back to draft). Delegates the status
 * transition to `postService.rejectPendingPost` (tier-agnostic, reused as-is)
 * — this wrapper only adds the app-layer owner guard, mirroring
 * `approvePendingPost`. Without it any account member could bounce another
 * member's pending post back to draft, which is a member-visible action on a
 * post they don't own; the owner guard keeps the review queue owner-only.
 */
export async function rejectPendingPost(postId: string): Promise<ServiceResponse<PostRow>> {
  try {
    const owner = await isAccountOwner();
    if (!owner) {
      return { data: null, error: new ServiceError('FORBIDDEN', OWNER_ONLY_REJECT_ERROR) };
    }
  } catch (err) {
    return { data: null, error: ServiceError.fromSupabaseError(err) };
  }
  return postService.rejectPendingPost(postId);
}

export const accountMembersService = {
  fetchRoster,
  sendInvite,
  revokeInvite,
  removeMember,
  leaveTeam,
  lookupInviteByToken,
  signUpForInvite,
  acceptInvite,
  getPendingReviewPosts,
  updateMemberTrust,
  setRequireMemberApproval,
  approvePendingPost,
  rejectPendingPost,
};
